import ListRowsSkeleton from "@/components/skeletons/ListRowsSkeleton";
import { useEffect, useState } from "react";
import { Tag, Pencil, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useMerchantAliasStore } from "../store/useMerchantAliasStore";
import { INPUT_CLASS } from "../types/settings.types";

export default function MerchantAliasesTab() {
  const { items, page, pageSize, total, loading, fetchAliases, upsertAlias } =
    useMerchantAliasStore();

  // Create form state
  const [newRawName, setNewRawName] = useState("");
  const [newAlias, setNewAlias] = useState("");
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);

  // Inline edit state
  const [editingRawName, setEditingRawName] = useState<string | null>(null);
  const [editingAlias, setEditingAlias] = useState("");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAliases(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmedRaw = newRawName.trim();
    const trimmedAlias = newAlias.trim();
    if (!trimmedAlias) {
      setCreateError("Alias cannot be empty.");
      return;
    }
    if (!trimmedRaw) {
      setCreateError("Raw name cannot be empty.");
      return;
    }
    setCreateError("");
    setCreating(true);
    try {
      await upsertAlias(trimmedRaw, trimmedAlias);
      setNewRawName("");
      setNewAlias("");
      fetchAliases(1);
    } finally {
      setCreating(false);
    }
  }

  function startEdit(rawName: string, currentAlias: string) {
    setEditingRawName(rawName);
    setEditingAlias(currentAlias);
    setEditError("");
  }

  function cancelEdit() {
    setEditingRawName(null);
    setEditingAlias("");
    setEditError("");
  }

  async function handleSaveEdit(rawName: string) {
    const trimmed = editingAlias.trim();
    if (!trimmed) {
      setEditError("Alias cannot be empty.");
      return;
    }
    setEditError("");
    setSaving(true);
    try {
      await upsertAlias(rawName, trimmed);
      setEditingRawName(null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Merchant Aliases
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create form */}
        <form onSubmit={handleCreate} className="space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              className={INPUT_CLASS + " sm:flex-1 min-w-0"}
              placeholder="Raw name (e.g. GRAB*FOOD)"
              value={newRawName}
              onChange={(e) => setNewRawName(e.target.value)}
              disabled={creating}
            />
            <input
              className={INPUT_CLASS + " sm:flex-1 min-w-0"}
              placeholder="Alias (e.g. Grab Food)"
              value={newAlias}
              onChange={(e) => {
                setNewAlias(e.target.value);
                if (createError) setCreateError("");
              }}
              disabled={creating}
            />
            <Button
              type="submit"
              size="sm"
              loading={creating}
              className="shrink-0"
            >
              {creating ? "Adding…" : "Add Alias"}
            </Button>
          </div>
          {createError && (
            <p className="text-xs text-destructive">{createError}</p>
          )}
        </form>

        {/* Table / empty state */}
        {loading ? (
          <ListRowsSkeleton rows={3} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No aliases yet — use the form above or rename a payee from the
            transaction list.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm table-fixed sm:table-auto">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-left py-2 pr-4 font-medium">
                      Raw Name
                    </th>
                    <th className="text-left py-2 pr-4 font-medium">Alias</th>
                    <th className="text-right py-2 pr-4 font-medium">
                      Matched
                    </th>
                    <th className="hidden sm:table-cell text-right py-2 font-medium">
                      Last Updated
                    </th>
                    <th className="w-16" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const isEditing = editingRawName === item.rawName;
                    return (
                      <tr
                        key={item.rawName}
                        className="group/row border-b border-border/50 last:border-0"
                      >
                        <td className="py-2.5 pr-4 text-muted-foreground font-mono text-xs break-all">
                          {item.rawName}
                        </td>
                        <td className="py-2.5 pr-4 break-words">
                          {isEditing ? (
                            <div className="space-y-1">
                              <input
                                className={INPUT_CLASS + " w-full"}
                                value={editingAlias}
                                onChange={(e) => {
                                  setEditingAlias(e.target.value);
                                  if (editError) setEditError("");
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    handleSaveEdit(item.rawName);
                                  if (e.key === "Escape") cancelEdit();
                                }}
                                autoFocus
                                disabled={saving}
                              />
                              {editError && (
                                <p className="text-xs text-destructive">
                                  {editError}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="font-medium text-foreground">
                              {item.alias}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-4 text-right [font-variant-numeric:tabular-nums]">
                          {item.matchedCount}
                        </td>
                        <td className="hidden sm:table-cell py-2.5 text-right text-muted-foreground [font-variant-numeric:tabular-nums]">
                          {new Date(item.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="py-2.5 pl-2">
                          {isEditing ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleSaveEdit(item.rawName)}
                                disabled={saving}
                                className="p-1 rounded hover:bg-muted text-primary"
                                title="Save"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={saving}
                                className="p-1 rounded hover:bg-muted text-muted-foreground"
                                title="Cancel"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() =>
                                startEdit(item.rawName, item.alias)
                              }
                              className="invisible group-hover/row:visible [@media(hover:none)]:visible p-1 rounded hover:bg-muted text-muted-foreground"
                              title="Edit alias"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => fetchAliases(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => fetchAliases(page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
